import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import * as PhosphorIcons from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const { MusicNotes: MusicNotesSimple, ArrowLeft, WifiSlash, EnvelopeSimple, Warning } = PhosphorIcons;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT_EMAIL = 'admin@musicsync.net';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [backendReachable, setBackendReachable] = useState(true);
  const [activeSessionPrompt, setActiveSessionPrompt] = useState(false);
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  // If already authenticated, show a friendly "you're already signed in"
  // panel instead of auto-redirecting — so if someone deliberately hits
  // /login expecting to sign in as a different user, they get a clear
  // message rather than being silently bounced. They can either go to
  // their dashboard or log out first. (Rule (2) — same browser, different
  // user requires an explicit log-out.)
  // Note: we DO auto-redirect from the root landing page; only /login
  // itself shows this informational screen.
  const showAlreadySignedInPanel = !!(user && user !== false && !isAdmin);

  // Health check on mount — detects unreachable backend and disables the login button
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await axios.get(`${API}/health`, { timeout: 6000 });
        if (alive) setBackendReachable(true);
      } catch (e) {
        if (alive) setBackendReachable(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Same-browser cross-user guard is now enforced by the
  // `already-signed-in-panel` — if a user is authenticated in this browser,
  // /login won't even render the form; it shows the panel with a
  // "Log out & sign in as a different user" button. The user must click it
  // first, which clears auth state, after which the form appears. That is
  // how rule (2) — "one browser = one active session" — is enforced.

  const doLogin = async (forceSession) => {
    setIsLoading(true);
    const result = await login(email, password, isAdmin, forceSession);
    setIsLoading(false);

    if (result.success) {
      toast.success('Login successful');
      if (result.role === 'kiosk' && result.store_id) {
        navigate(`/kiosk/${result.store_id}`);
      } else if (result.role === 'kiosk') {
        navigate('/player');
      } else {
        navigate('/dashboard');
      }
      return;
    }
    if (result.activeSessionExists) {
      // Prompt — user decides whether to evict the other device.
      setActiveSessionPrompt(true);
      return;
    }
    if (result.blocked) {
      navigate('/expired');
    } else {
      toast.error(result.error);
      if (result.networkError) setBackendReachable(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!backendReachable) {
      toast.error(`Backend is unreachable. Please try again or email ${SUPPORT_EMAIL}`);
      return;
    }
    await doLogin(false);
  };

  const handleConfirmEndOtherSession = async () => {
    setActiveSessionPrompt(false);
    await doLogin(true);
  };

  return (
    <div className="min-h-screen bg-[#f5f0e8] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md flex-1 flex flex-col justify-center">
        <div className="mb-8">
          <Button onClick={() => navigate('/')} className="bg-transparent hover:bg-white/50 text-[#8a8477] border-0" data-testid="back-to-home">
            <ArrowLeft size={20} className="mr-2" />Back to Home
          </Button>
        </div>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600 mb-4">
            <MusicNotesSimple size={32} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-[#2d2a26] mb-2" data-testid="login-title">Welcome Back</h1>
          <p className="text-[#8a8477] font-mono text-sm tracking-wider uppercase">Login to Your Account</p>
        </div>

        {!backendReachable && (
          <div
            className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
            data-testid="backend-unreachable-banner"
            role="alert"
          >
            <WifiSlash size={20} weight="bold" className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Server unreachable</p>
              <p className="mt-0.5">
                We couldn't reach the MusicSync server. Check your internet and try again. If the problem persists, email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="underline font-medium">{SUPPORT_EMAIL}</a>.
              </p>
            </div>
          </div>
        )}

        {showAlreadySignedInPanel ? (
          <div
            className="bg-white border border-[#d9d3c5] rounded-xl p-6 shadow-sm text-center"
            data-testid="already-signed-in-panel"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-3">
              <Warning size={22} weight="bold" className="text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-[#2d2a26] mb-2">You're already signed in</h2>
            <p className="text-sm text-[#6b665c] mb-4">
              This browser is signed in as <strong className="text-[#2d2a26]">{user.email}</strong>. To sign in as a
              different user, log out first. Only one active session is allowed per browser at a time.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => {
                  if (user.role === 'kiosk' && user.store_id) navigate(`/kiosk/${user.store_id}`);
                  else navigate('/dashboard');
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0"
                data-testid="go-to-dashboard-btn"
              >
                Continue as {user.email}
              </Button>
              <Button
                onClick={async () => { await logout(); toast.success('Signed out'); }}
                className="w-full bg-white hover:bg-[#ede8dc] text-[#6b665c] border border-[#d9d3c5]"
                data-testid="logout-and-switch-user-btn"
              >
                Log out &amp; sign in as a different user
              </Button>
            </div>
          </div>
        ) : (
          <>
        <div className="flex gap-2 mb-6">
          <Button onClick={() => setIsAdmin(false)}
            className={`flex-1 transition-all duration-200 ${!isAdmin ? 'bg-blue-600 hover:bg-blue-700 text-white border-0' : 'bg-white hover:bg-[#ede8dc] text-[#6b665c] border border-[#d9d3c5]'}`}
            data-testid="tenant-login-tab">Tenant Login</Button>
          <Button onClick={() => setIsAdmin(true)}
            className={`flex-1 transition-all duration-200 ${isAdmin ? 'bg-red-600 hover:bg-red-700 text-white border-0' : 'bg-white hover:bg-[#ede8dc] text-[#6b665c] border border-[#d9d3c5]'}`}
            data-testid="admin-login-tab">Admin Login</Button>
        </div>

        <div className="bg-white border border-[#d9d3c5] rounded-xl p-6 shadow-sm">
          <form onSubmit={handleSubmit} data-testid="login-form">
            <div className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-[#6b665c] font-mono text-xs uppercase tracking-wider">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 bg-[#f5f0e8] border-[#d9d3c5] text-[#2d2a26]" required data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password" className="text-[#6b665c] font-mono text-xs uppercase tracking-wider">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 bg-[#f5f0e8] border-[#d9d3c5] text-[#2d2a26]" required data-testid="login-password-input" />
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0 h-11 disabled:opacity-50"
                disabled={isLoading || !backendReachable}
                data-testid="login-submit-button"
              >
                {isLoading ? 'Logging in...' : !backendReachable ? 'Server unreachable' : 'Login'}
              </Button>
            </div>
          </form>
          {!isAdmin && (
            <div className="mt-4 text-center">
              <button onClick={() => navigate('/forgot-password')} className="text-sm text-blue-600 hover:text-blue-700" data-testid="forgot-password-link">Forgot Password?</button>
            </div>
          )}
        </div>

        {!isAdmin && (
          <div className="mt-6 text-center text-sm text-[#8a8477]">
            <p>Don't have an account? <button onClick={() => navigate('/register')} className="text-blue-600 hover:text-blue-700 font-medium">Sign up</button></p>
          </div>
        )}
        {isAdmin && (
          <div className="mt-6 text-center text-xs text-[#8a8477] font-mono"><p>Global Admin Access Only</p></div>
        )}
          </>
        )}
      </div>

      {/* End-other-session confirm dialog — Discord / Netflix-style UX */}
      <Dialog open={activeSessionPrompt} onOpenChange={setActiveSessionPrompt}>
        <DialogContent className="bg-white border-[#d9d3c5] text-[#2d2a26] max-w-md" data-testid="active-session-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <Warning size={18} weight="bold" className="text-amber-600" />
              Already signed in elsewhere
            </DialogTitle>
            <DialogDescription className="sr-only">
              This account is currently signed in on another device. Confirm to end that session and sign in here, or cancel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5a554d]">
              This account is currently signed in on another device or browser. For your security, only one active session is allowed at a time.
            </p>
            <p className="text-sm text-[#5a554d]">
              Do you want to end that session and sign in here?
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => setActiveSessionPrompt(false)}
                disabled={isLoading}
                data-testid="active-session-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmEndOtherSession}
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="active-session-confirm-btn"
              >
                {isLoading ? 'Signing in…' : 'End other session & sign in here'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Support email + legal footer */}
      <footer className="w-full max-w-md mt-8 pt-4 border-t border-[#d9d3c5]/60 text-center space-y-2" data-testid="login-footer">
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="inline-flex items-center gap-2 text-xs text-[#8a8477] font-mono hover:text-[#2d2a26] transition-colors"
          data-testid="login-support-email"
        >
          <EnvelopeSimple size={14} />
          Need help? {SUPPORT_EMAIL}
        </a>
        <div className="flex items-center justify-center gap-3 text-xs font-mono text-[#8a8477]">
          <a href="/privacy-policy" className="hover:text-[#2d2a26]" data-testid="login-privacy-link">Privacy</a>
          <span>·</span>
          <a href="/terms-of-service" className="hover:text-[#2d2a26]" data-testid="login-terms-link">Terms</a>
        </div>
      </footer>
    </div>
  );
};

export default Login;
